<template>
    <div class="xl:pl-4 my-8 xl:mt-0 w-full xl:w-2/5">
        <h2 class="mb-2">Chain Pricing</h2>

        <div class="flex space-x-2 text-sm my-1">
            <div><span class="font-bold">$: </span> Set dollar amount</div>
            <div><span class="font-bold">$+: </span>Dollars above cost</div>
            <div><span class="font-bold">%: </span> Percentage below MSRP</div>
            <div><span class="font-bold">M%: </span> Margin percentage</div>
        </div>

        <div class="rounded-t overflow-auto border border-b-0" v-show="form.pricing.length > 0">
            <table class="table w-full">
                <thead>
                    <tr>
                        <th v-if="chainId" scope="col" class="w-8 p-0 align-middle">
                            <span class="sr-only">Favourite</span>
                        </th>
                        <th class="text-left whitespace-nowrap">Item #</th>
                        <th class="text-left">Product</th>
                        <th class="text-left">UofM</th>
                        <th class="text-right">Cost</th>
                        <th class="text-right">Base</th>
                        <th class="text-left">Price</th>
                        <th class="text-right"></th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="pricing in sortedPricing" :key="pricing.id" :class="{ 'bg-gray-300': !getProductEnabled(pricing.product_id) }">
                        <td v-if="chainId" class="w-8 align-middle">
                            <BetterTooltip placement="bottom" floating-pointer-events-none disable-flip>
                                <template #reference>
                                    <button
                                        type="button"
                                        class="favourite-star-btn w-6 h-6 inline-flex items-center justify-center rounded text-gray-400 hover:text-yellow-500 border-0 bg-transparent p-0"
                                        :class="{ 'text-yellow-500': hasAnyMemberFavourite(pricing.product_id) }"
                                        @click.prevent="openFavouriteModal(pricing)"
                                    >
                                        <i
                                            class="text-xs"
                                            :class="hasAnyMemberFavourite(pricing.product_id) ? 'fas fa-star' : 'far fa-star'"
                                            aria-hidden="true"
                                        ></i>
                                    </button>
                                </template>
                                <template #floating>
                                    <div class="text-[11px] whitespace-nowrap">Chain member favourites</div>
                                </template>
                            </BetterTooltip>
                        </td>
                        <td class="leading-tight text-sm">{{ pricing.product_id }}</td>
                        <td class="leading-tight text-sm">{{ pricing.title }}</td>
                        <td class="leading-tight text-sm">{{ pricing.size }}</td>
                        <td class="text-right text-sm">{{ pricing.market_cost ? toCurrency(pricing.market_cost) : toCurrency(pricing.cost) }}</td>
                        <td class="text-right text-sm">{{ toCurrency(pricing.base_price) }}</td>
                        <td class="text-left">
                            <div class="inline-flex justify-end">
                                <select v-model="form.pricing[pricing.orig_index].modifier_type" class="w-[4.1rem   ] h-7 rounded-l rounded-r-none text-sm">
                                    <option value="fixed">$</option>
                                    <option value="cost_plus">$+</option>
                                    <option value="percent_off_price">%</option>
                                    <option value="margin">M%</option>
                                </select>

                                <input
                                    v-model.number="form.pricing[pricing.orig_index].modifier_amount"
                                    type="number"
                                    step="any"
                                    class="w-16 h-7 border-l-0 text-sm rounded-l-none rounded-r"
                                />
                            </div>

                            <jet-input-error :message="form.errors[`pricing.${pricing.orig_index}.price`]" />
                            <jet-input-error :message="form.errors[`pricing.${pricing.orig_index}.modifier_type`]" />
                            <jet-input-error :message="form.errors[`pricing.${pricing.orig_index}.modifier_amount`]" />
                        </td>
                        <td class="text-right text-sm">
                            <span v-if="form.pricing[pricing.orig_index].price !== null">
                                {{ isNaN(form.pricing[pricing.orig_index].price) ? '...' : toCurrency(form.pricing[pricing.orig_index].price) }}
                            </span>
                        </td>
                        <td>
                            <jet-secondary-button
                                class="leading-none py-1 px-2 align-middle"
                                @click="remove(pricing.product_id)"
                            >
                                <img src="/images/icons/trash_w.svg" width="18" height="18" />
                            </jet-secondary-button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="flex items-center gap-x-3 mt-2">
            <ProductMultiselect
                ref="productSelect"
                @select="onProductSelected"
                :product-options="productOptions"
                :loading="loadingProducts"
                placeholder="Select Product"
                class="h-8"
                style="min-height: 20px"
            >
                <template #option="{ option }">
                    <span
                        :class="{
                            'text-gray-400' : option.enabled == false,
                        }"
                    >
                        <span>{{ option.id }} - </span>{{ option.title }}
                        <span class="text-xs italic text-red" v-if="option.special_order">(Special Order Item)</span>
                    </span>
                </template>
            </ProductMultiselect>

            <jet-secondary-button type="button" @click="add" :disabled="selectedProduct === null">
                Add
            </jet-secondary-button>
        </div>

        <jet-dialog-modal :show="favouriteModalOpen" :closeable="true" @close="closeFavouriteModal" max-width="2xl">
            <template #title>
                <span>{{ favouriteModalTitle }}</span>
            </template>

            <template #content>
                <div v-if="!chainCustomers.length" class="text-sm text-gray-600">
                    Add customers to this chain to manage favourites.
                </div>
                <div v-else class="overflow-auto border rounded">
                    <table class="table w-full text-sm">
                        <thead>
                            <tr>
                                <th class="text-left">Customer</th>
                                <th scope="col" class="w-10 p-0 align-middle">
                                    <span class="sr-only">Favourite</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="c in chainCustomers" :key="'fav-' + c.id">
                                <td class="align-middle">{{ c.name }}</td>
                                <td class="w-10 align-middle">
                                    <BetterTooltip placement="left-start" floating-pointer-events-none disable-flip>
                                        <template #reference>
                                            <button
                                                type="button"
                                                class="favourite-star-btn w-6 h-6 inline-flex items-center justify-center rounded text-gray-400 hover:text-yellow-500 border-0 bg-transparent p-0"
                                                :class="{ 'text-yellow-500': isCustomerFavourite(favouriteModalProductId, c.id) }"
                                                @click.prevent="toggleCustomerFavourite(c.id)"
                                            >
                                                <i
                                                    class="text-xs"
                                                    :class="isCustomerFavourite(favouriteModalProductId, c.id) ? 'fas fa-star' : 'far fa-star'"
                                                    aria-hidden="true"
                                                ></i>
                                            </button>
                                        </template>
                                        <template #floating>
                                            <div class="text-[11px] whitespace-nowrap">
                                                {{ isCustomerFavourite(favouriteModalProductId, c.id) ? 'Remove from Favourites' : 'Add to Favourites' }}
                                            </div>
                                        </template>
                                    </BetterTooltip>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </template>

            <template #footer>
                <jet-secondary-button
                    type="button"
                    class="mr-2"
                    :disabled="!chainCustomers.length"
                    @click="favouriteForAll"
                >
                    Favourite for all
                </jet-secondary-button>
                <jet-tertiary-button type="button" @click="closeFavouriteModal">
                    Close
                </jet-tertiary-button>
            </template>
        </jet-dialog-modal>
    </div>
</template>

<script>
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetDialogModal from '@/Jetstream/DialogModal';
import BetterTooltip from '@/Jetstream/BetterTooltip';
import ProductMultiselect from '@/Shared/ProductMultiSelect.vue';
import { calculatePricingLevel } from '@/helper';

export default {
    components: {
        JetInput,
        JetInputError,
        JetSecondaryButton,
        JetTertiaryButton,
        JetDialogModal,
        ProductMultiselect,
        BetterTooltip,
    },

    props: {
        form: { type: Object, required: true },
        chainId: { type: [Number, String], default: null },
        /** Absolute URL for JSON catalogue (same shape as former Inertia `products` prop). */
        pricingProductsUrl: { type: String, required: true },
    },

    data () {
        return {
            selectedProduct: null,
            favouriteModalOpen: false,
            favouriteModalProductId: null,
            favouriteModalTitle: '',
            products: [],
            loadingProducts: true,
        }
    },

    mounted () {
        this.fetchPricingProducts();
    },

    computed: {
        chainCustomers () {
            return this.form.customers || [];
        },

        productOptions () {
            const unusedProducts = this.products.filter(p => {
                return this.form.pricing.findIndex(p2 => p2.product_id == p.id) === -1;
            });

            return unusedProducts.map(p => {
                let title = p.title;

                if (p.description) title += ' - ' + p.description;
                if (p.size) title += ' - ' + p.size;

                return {
                    id: p.id,
                    title: title,
                    special_order: p.special_order,
                    special_order_lead_hours: p.special_order_lead_hours,
                    enabled: p.enabled,
                }
            });
        },

        sortedPricing () {
            let sortedPricing = JSON.parse(JSON.stringify(this.form.pricing));
            sortedPricing.forEach((element, index) => {
                element.orig_index = index;
            });

            return sortedPricing.sort((a, b) => {
                if (a.title < b.title)return -1;
                if (a.title > b.title) return 1;
                return 0;
            });
        }
    },

    methods: {
        ensureFavouritesMap () {
            if (!this.form.favourites_by_product || typeof this.form.favourites_by_product !== 'object') {
                this.form.favourites_by_product = {};
            }
        },

        cloneFavouritesMap () {
            this.ensureFavouritesMap();
            const src = this.form.favourites_by_product;
            const out = {};
            for (const key of Object.keys(src)) {
                const arr = src[key];
                if (Array.isArray(arr)) {
                    out[key] = arr.map(id => Number(id));
                }
            }
            return out;
        },

        favCustomerIdsForProduct (productId) {
            this.ensureFavouritesMap();
            const src = this.form.favourites_by_product;
            const pid = Number(productId);
            const list = src[pid] ?? src[String(pid)];
            if (!Array.isArray(list)) {
                return [];
            }
            return list.map(id => Number(id));
        },

        memberIdSet () {
            return new Set((this.form.customers || []).map(c => Number(c.id)));
        },

        hasAnyMemberFavourite (productId) {
            const favIds = this.favCustomerIdsForProduct(productId);
            const members = this.memberIdSet();
            return favIds.some(id => members.has(Number(id)));
        },

        isCustomerFavourite (productId, customerId) {
            if (productId === null || productId === undefined) {
                return false;
            }
            const cid = Number(customerId);
            return this.favCustomerIdsForProduct(productId).some(id => Number(id) === cid);
        },

        openFavouriteModal (pricing) {
            if (!this.chainId) {
                return;
            }
            this.favouriteModalProductId = pricing.product_id;
            this.favouriteModalTitle = pricing.title;
            this.favouriteModalOpen = true;
        },

        closeFavouriteModal () {
            this.favouriteModalOpen = false;
            this.favouriteModalProductId = null;
            this.favouriteModalTitle = '';
        },

        toggleCustomerFavourite (customerId) {
            const productId = this.favouriteModalProductId;
            const cid = Number(customerId);
            const pid = Number(productId);
            if (!this.chainId || !Number.isFinite(pid) || !Number.isFinite(cid)) {
                return;
            }
            const map = this.cloneFavouritesMap();
            const key = String(pid);
            let list = [...(map[key] || [])];
            const idx = list.indexOf(cid);
            if (idx === -1) {
                list.push(cid);
            } else {
                list.splice(idx, 1);
            }
            if (list.length === 0) {
                delete map[key];
            } else {
                map[key] = list;
            }
            this.form.favourites_by_product = { ...map };
        },

        favouriteForAll () {
            const productId = this.favouriteModalProductId;
            const pid = Number(productId);
            if (!this.chainId || !Number.isFinite(pid) || !this.chainCustomers.length) {
                return;
            }
            const map = this.cloneFavouritesMap();
            const key = String(pid);
            map[key] = this.chainCustomers.map(c => Number(c.id));
            this.form.favourites_by_product = { ...map };
        },

        remove (productId) {
            const pid = productId;
            const index = this.form.pricing.findIndex(p => p.product_id === pid);

            if (index !== -1) {
                this.form.pricing.splice(index, 1);
            }

            const map = this.cloneFavouritesMap();
            delete map[String(pid)];
            this.form.favourites_by_product = { ...map };
        },

        add () {
            const product = this.products.find(p => p.id === this.selectedProduct);

            if (!product) {
                return;
            }

            this.form.pricing.push({
                product_id: this.selectedProduct,
                price: null,
                base_price: product.price,
                cost: product.cost,
                market_cost: product.market_cost,
                title: product.title,
                size: product.size,
            });

            this.selectedProduct = null;
            this.$refs.productSelect.clear();
        },

        calculatePricingLevel (pricingLevel, cost, price) {
            return this.roundDecimals(calculatePricingLevel(pricingLevel, cost, price));
        },

        getProductEnabled (productId) {
            if (this.loadingProducts) {
                return true;
            }
            const product = this.products.find(p => p.id === productId);
            return product && product.enabled ? true : false;
        },

        fetchPricingProducts () {
            this.loadingProducts = true;
            axios
                .get(this.pricingProductsUrl, {
                    headers: {
                        'Cache-Control': 'no-cache',
                        'Pragma': 'no-cache',
                        'Expires': '0',
                    },
                })
                .then(response => {
                    this.products = response.data;
                    this.$nextTick(() => {
                        this.loadingProducts = false;
                    });
                })
                .catch(() => {
                    this.loadingProducts = false;
                });
        },

        onProductSelected(productId) {
            this.selectedProduct = productId;
        },
    },

    watch:{
        'form.pricing':{
            handler(newValue) {
                this.$nextTick(() => {
                    for (let pricing of newValue){
                        const effectiveCost = pricing.market_cost ?? pricing.cost;
                        pricing.price = this.calculatePricingLevel(pricing, effectiveCost, pricing.base_price)
                    }
                });
                
            },
            deep: true
        }
    }
}
</script>

<style scoped>
    input[type="number"] {
        @apply py-0 px-1;
        max-width: 75px;
    }

    tr.bg-gray-300 td {
        @apply bg-gray-300;
    }
</style>